import type { PulsePeriod } from "./pulse-period";
import {
  DEFAULT_FREQUENCY_DAYS,
  EPOCH_ANCHOR_MS,
  MS_PER_DAY,
  addDaysISO,
  getCheckInPeriodForDate,
  getPeriodForDate,
  toISODate,
} from "./pulse-period";

export interface PulseConfigPending {
  checkInFrequencyDays: number;
  periodStartWeekday: number;
  effectiveFrom: string;
  requestedAt: string;
  requestedBy: string;
}

export interface PulseConfig {
  checkInFrequencyDays: number;
  configuredAt: string;
  periodStartWeekday: number;
  periodAnchor: string;
  bootstrapPeriod?: PulsePeriod;
  pending?: PulseConfigPending;
}

export const PULSE_WEEKDAY_LABELS = [
  "Söndag",
  "Måndag",
  "Tisdag",
  "Onsdag",
  "Torsdag",
  "Fredag",
  "Lördag",
] as const;

export function isPulseConfigured(
  config?: Partial<PulseConfig> | null,
): config is PulseConfig {
  return config?.configuredAt != null && config.periodAnchor != null;
}

export function getFrequencyDays(config?: Partial<PulseConfig> | null): number {
  return config?.checkInFrequencyDays ?? DEFAULT_FREQUENCY_DAYS;
}

export function getWeekdayUTC(isoDate: string): number {
  return new Date(`${isoDate}T00:00:00.000Z`).getUTCDay();
}

/** Next calendar date on or after `from` with the given weekday (0=Sun … 6=Sat). */
export function computePeriodAnchor(
  from: Date,
  periodStartWeekday: number,
): string {
  let cursor = toISODate(from);
  for (let i = 0; i < 7; i++) {
    if (getWeekdayUTC(cursor) === periodStartWeekday) {
      return cursor;
    }
    cursor = addDaysISO(cursor, 1);
  }
  return toISODate(from);
}

/** Past X days ending today (periodEnd exclusive = tomorrow). */
export function getBootstrapPeriod(
  today: Date,
  frequencyDays: number,
): PulsePeriod {
  const endExclusive = addDaysISO(toISODate(today), 1);
  const start = addDaysISO(endExclusive, -frequencyDays);
  return { periodStart: start, periodEnd: endExclusive };
}

export function anchorMsFromConfig(config: PulseConfig): number {
  return Date.parse(`${config.periodAnchor}T00:00:00.000Z`);
}

export function getNextPeriodStart(
  config: PulseConfig,
  after: Date = new Date(),
): string {
  const anchorMs = anchorMsFromConfig(config);
  const current = getPeriodForDate(
    after,
    config.checkInFrequencyDays,
    anchorMs,
  );
  return current.periodEnd;
}

export function schedulePulseConfigChange(
  active: PulseConfig,
  patch: Pick<PulseConfigPending, "checkInFrequencyDays" | "periodStartWeekday">,
  requestedAt: string,
  requestedBy: string,
  today: Date = new Date(),
): PulseConfigPending {
  return {
    ...patch,
    effectiveFrom: getNextPeriodStart(active, today),
    requestedAt,
    requestedBy,
  };
}

export function applyPendingIfDue(
  config: PulseConfig,
  today: Date = new Date(),
): PulseConfig {
  if (!config.pending) return config;
  const todayIso = toISODate(today);
  if (todayIso < config.pending.effectiveFrom) return config;

  return {
    ...config,
    checkInFrequencyDays: config.pending.checkInFrequencyDays,
    periodStartWeekday: config.pending.periodStartWeekday,
    periodAnchor: config.pending.effectiveFrom,
    pending: undefined,
  };
}

export type MigrateLegacyPulseOptions = {
  /** When true, keep global epoch grid so existing scores stay addressable. */
  useEpochGrid?: boolean;
};

export function migrateLegacyPulseConfig(
  raw: Partial<PulseConfig> | null | undefined,
  options: MigrateLegacyPulseOptions = {},
): Partial<PulseConfig> | null | undefined {
  if (!raw) return raw;
  if (raw.configuredAt) return raw;
  if (raw.checkInFrequencyDays == null) return raw;

  const freq = raw.checkInFrequencyDays;
  if (options.useEpochGrid) {
    const period = getPeriodForDate(new Date(), freq, EPOCH_ANCHOR_MS);
    return {
      ...raw,
      configuredAt: "2024-01-01T00:00:00.000Z",
      periodStartWeekday: 1,
      periodAnchor: period.periodStart,
    };
  }

  const now = new Date();
  const weekday = raw.periodStartWeekday ?? 1;
  return {
    ...raw,
    configuredAt: now.toISOString(),
    periodStartWeekday: weekday,
    periodAnchor: computePeriodAnchor(now, weekday),
  };
}

export function buildInitialBusinessPulseConfig(
  checkInFrequencyDays: number,
  periodStartWeekday: number,
  configuredAt: Date = new Date(),
): PulseConfig {
  const periodAnchor = computePeriodAnchor(configuredAt, periodStartWeekday);
  return {
    checkInFrequencyDays,
    configuredAt: configuredAt.toISOString(),
    periodStartWeekday,
    periodAnchor,
    bootstrapPeriod: getBootstrapPeriod(configuredAt, checkInFrequencyDays),
  };
}

export function buildInitialTeamPulseConfig(
  checkInFrequencyDays: number,
  periodStartWeekday: number,
  configuredAt: Date = new Date(),
): PulseConfig {
  return {
    checkInFrequencyDays,
    configuredAt: configuredAt.toISOString(),
    periodStartWeekday,
    periodAnchor: computePeriodAnchor(configuredAt, periodStartWeekday),
  };
}

export function getEffectivePulseConfig(
  businessRaw: Partial<PulseConfig> | null | undefined,
  teamRaw?: Partial<PulseConfig> | null,
  today: Date = new Date(),
): PulseConfig | null {
  const businessMigrated = migrateLegacyPulseConfig(businessRaw);
  if (!isPulseConfigured(businessMigrated)) return null;

  const business = applyPendingIfDue(
    businessMigrated as PulseConfig,
    today,
  );

  if (!teamRaw || !isPulseConfigured(teamRaw)) {
    return business;
  }

  const team = applyPendingIfDue(teamRaw as PulseConfig, today);
  return {
    ...team,
    bootstrapPeriod: business.bootstrapPeriod,
  };
}

export function periodsMatch(a: PulsePeriod, b: PulsePeriod): boolean {
  return a.periodStart === b.periodStart && a.periodEnd === b.periodEnd;
}

export function getCheckInPeriodForConfig(
  config: PulseConfig,
  date: Date = new Date(),
  bootstrapCompleted: boolean,
): PulsePeriod {
  if (config.bootstrapPeriod && !bootstrapCompleted) {
    return config.bootstrapPeriod;
  }
  const anchorMs = anchorMsFromConfig(config);
  return getCheckInPeriodForDate(
    date,
    config.checkInFrequencyDays,
    anchorMs,
  );
}

export function isPeriodAllowedForCheckIn(
  config: PulseConfig,
  period: PulsePeriod,
  date: Date,
  bootstrapCompleted: boolean,
): boolean {
  const active = getCheckInPeriodForConfig(config, date, bootstrapCompleted);
  if (periodsMatch(period, active)) return true;
  if (
    config.bootstrapPeriod &&
    !bootstrapCompleted &&
    periodsMatch(period, config.bootstrapPeriod)
  ) {
    return true;
  }
  return false;
}

function periodHasEnded(period: PulsePeriod, date: Date): boolean {
  return toISODate(date) >= period.periodEnd;
}

/** In-flight grid period (not yet eligible for check-in). */
export function getInFlightPeriodForConfig(
  config: PulseConfig,
  date: Date = new Date(),
): PulsePeriod | null {
  const anchorMs = anchorMsFromConfig(config);
  const inFlight = getPeriodForDate(
    date,
    config.checkInFrequencyDays,
    anchorMs,
  );
  return periodHasEnded(inFlight, date) ? null : inFlight;
}

/** Ended recurring periods from periodAnchor forward, newest first. */
export function listEndedRecurringPeriodsFromAnchor(
  config: PulseConfig,
  date: Date = new Date(),
  maxCount = 26,
): PulsePeriod[] {
  const anchorMs = anchorMsFromConfig(config);
  const freq = config.checkInFrequencyDays;
  const lengthMs = freq * MS_PER_DAY;
  const todayIso = toISODate(date);
  const ended: PulsePeriod[] = [];

  // Walk the whole grid up to today and keep a sliding window of the latest
  // ended periods. Stopping once the window filled up returned the OLDEST
  // periods after the anchor instead, so every history view froze
  // `maxCount` periods after configuration.
  let startMs = anchorMs;
  for (;;) {
    const endMs = startMs + lengthMs;
    const period: PulsePeriod = {
      periodStart: toISODate(new Date(startMs)),
      periodEnd: toISODate(new Date(endMs)),
    };
    if (period.periodEnd > todayIso) break;
    if (period.periodStart >= config.periodAnchor) {
      ended.push(period);
      if (ended.length > maxCount) ended.shift();
    }
    startMs = endMs;
  }

  return ended.reverse();
}

/**
 * Periods shown in the Pulse overview strip (newest first).
 * New businesses only see bootstrap until done, then ended windows from anchor forward.
 */
export function listRecentPeriodsForConfig(
  config: PulseConfig,
  count: number,
  date: Date = new Date(),
  bootstrapCompleted = true,
): PulsePeriod[] {
  if (config.bootstrapPeriod && !bootstrapCompleted) {
    return [config.bootstrapPeriod];
  }

  const endedRecurring = listEndedRecurringPeriodsFromAnchor(
    config,
    date,
    count,
  );
  const results: PulsePeriod[] = [];

  if (config.bootstrapPeriod && bootstrapCompleted) {
    const duplicatesGrid = endedRecurring.some((p) =>
      periodsMatch(p, config.bootstrapPeriod!),
    );
    if (!duplicatesGrid) {
      results.push(config.bootstrapPeriod);
    }
  }

  for (const p of endedRecurring) {
    if (!results.some((r) => periodsMatch(r, p))) {
      results.push(p);
    }
  }

  // Newest first: the bootstrap window predates the grid, so it belongs at the
  // end and drops off once enough recurring periods have ended.
  results.sort((a, b) => b.periodStart.localeCompare(a.periodStart));
  return results.slice(0, count);
}
